import { Box, Button, Chip, Stack } from "@mui/material";
import { useOnboardingMode } from "../../hooks/use-onboarding-mode";

/**
 * Shared header for both `<ArgusOnboarding />` (v2 hero) and
 * `<BetterOnboarding />` (legacy wizard). Renders the desplega logo on the
 * left and the mode-toggle + skip button on the right.
 *
 * Header layout MUST stay identical between the two flows so switching
 * doesn't visually shift content — both wrap this in a 80px-tall flex-center
 * Box so the natural content height is irrelevant.
 *
 * The toggle button label flips based on current mode — clicking it commits
 * the opposite mode through `useOnboardingMode`. Both header instances react
 * because the hook broadcasts a same-window CustomEvent on every setMode. A
 * "NEW" chip appears only when the toggle points TO Argus (i.e. user is in
 * the legacy manual flow) — v2 is the new thing worth advertising.
 *
 * `onSkip` is passed in because each flow owns its own skip semantics (Argus
 * writes flags directly + navigates; BetterOnboarding routes through its
 * provider's `skip()`).
 */
export function OnboardingHeader({
  onSkip,
  skipDisabled = false,
}: {
  onSkip: () => void;
  skipDisabled?: boolean;
}) {
  const [mode, setOnboardingMode] = useOnboardingMode();
  const otherMode = mode === "manual" ? "argus" : "manual";
  const toggleLabel = otherMode === "manual" ? "Use manual onboarding" : "Use Argus onboarding";
  const showNewBadge = otherMode === "argus"; // we're in manual, advertising v2

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{ width: "100%", px: { xs: 3, md: 6 }, py: 2 }}
    >
      <Box component="img" src="/logo-blue.svg" alt="desplega.ai" sx={{ height: 42 }} />
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="text"
          size="small"
          onClick={() => setOnboardingMode(otherMode)}
          sx={{ color: "text.secondary" }}
          startIcon={
            showNewBadge ? (
              <Chip
                label="New"
                color="primary"
                size="small"
                sx={{ height: 18, "& .MuiChip-label": { fontSize: 10, px: 0.75 } }}
              />
            ) : undefined
          }
        >
          {toggleLabel}
        </Button>
        <Button variant="outlined" size="small" onClick={onSkip} disabled={skipDisabled}>
          Skip onboarding
        </Button>
      </Stack>
    </Stack>
  );
}
